// ============================================================
//  ALTIMO Medical Center — Habitaciones.jsx
//  Control de habitaciones + gestión de clínicas
//  Ubicación: src/components/Habitaciones.jsx
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';

const ESTADO_COLOR = {
  Libre:         { bg:'#d4edda', color:'#155724', border:'#c3e6cb' },
  Ocupada:       { bg:'#f8d7da', color:'#721c24', border:'#f5c6cb' },
  Limpieza:      { bg:'#fff3cd', color:'#856404', border:'#ffc107' },
  Mantenimiento: { bg:'#e2e3e5', color:'#383d41', border:'#d6d8db' },
  Otra:          { bg:'#e8f0fb', color:'#1B2A5E', border:'#b8cef5' },
};

const TIPO_COLOR = {
  UCI:      { bg:'#f8d7da', color:'#721c24' },
  Privada:  { bg:'#cce5ff', color:'#004085' },
  General:  { bg:'#d4edda', color:'#155724' },
  Pediatria:{ bg:'#fff3cd', color:'#856404' },
};

const TIPOS_HAB = ['UCI','Privada','General','Pediatria'];

const S = {
  pagina: { background:'#f0f4f8', minHeight:'100vh', padding:'20px', fontFamily:'Segoe UI, sans-serif' },
  card:   { background:'#fff', borderRadius:'10px', border:'1px solid #dde3ea', padding:'18px', marginBottom:'16px' },
  cardTitulo: {
    fontSize:'12px', fontWeight:'700', color:'#1B2A5E',
    textTransform:'uppercase', letterSpacing:'.05em',
    marginBottom:'14px', paddingBottom:'10px', borderBottom:'2px solid #e8f0fb',
    display:'flex', justifyContent:'space-between', alignItems:'center',
  },
  grid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'12px' },
  habCard: (estado) => ({
    border:`2px solid ${ESTADO_COLOR[estado]?.border||'#dde3ea'}`,
    borderRadius:'10px', padding:'14px', background:'#fff',
    cursor:'pointer', transition:'transform .1s',
  }),
  habNum:    { fontSize:'20px', fontWeight:'800', color:'#1B2A5E', marginBottom:'4px' },
  habTipo:   (tipo) => ({ display:'inline-block', padding:'2px 8px', borderRadius:'99px', fontSize:'10px', fontWeight:'700', background:TIPO_COLOR[tipo]?.bg||'#e2e3e5', color:TIPO_COLOR[tipo]?.color||'#333', marginBottom:'8px' }),
  habPrecio: { fontSize:'12px', color:'#6c757d', marginBottom:'8px' },
  estadoPill:(estado) => ({ display:'inline-block', padding:'3px 10px', borderRadius:'99px', fontSize:'11px', fontWeight:'700', background:ESTADO_COLOR[estado]?.bg||'#e2e3e5', color:ESTADO_COLOR[estado]?.color||'#333' }),
  btnEstado: (activo) => ({ padding:'7px 14px', borderRadius:'7px', fontSize:'12px', fontWeight:'600', cursor:'pointer', border:'none', background:activo?'#1B2A5E':'#f0f4f8', color:activo?'#fff':'#495057', marginRight:'6px', marginBottom:'6px' }),
  btnPrimario:  { background:'#1D9E75', color:'#fff', border:'none', borderRadius:'7px', padding:'9px 20px', fontSize:'13px', fontWeight:'700', cursor:'pointer', marginRight:'8px' },
  btnAzul:      { background:'#1B2A5E', color:'#fff', border:'none', borderRadius:'7px', padding:'7px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer', marginRight:'6px' },
  btnSecundario:{ background:'#fff', color:'#495057', border:'1px solid #c0ccda', borderRadius:'7px', padding:'8px 16px', fontSize:'12px', cursor:'pointer' },
  modal: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modalCard: { background:'#fff', borderRadius:'12px', padding:'28px', width:'420px', border:'1px solid #dde3ea', maxHeight:'90vh', overflowY:'auto' },
  input: { width:'100%', fontSize:'13px', padding:'8px 10px', border:'1px solid #c0ccda', borderRadius:'7px', background:'#fff', color:'#333', outline:'none', boxSizing:'border-box' },
  label: { display:'block', fontSize:'11px', fontWeight:'600', color:'#6c757d', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'.04em' },
  campo: { marginBottom:'12px' },
  resumen: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'16px' },
  kpi:    { background:'#fff', borderRadius:'8px', border:'1px solid #dde3ea', padding:'12px 16px', textAlign:'center' },
  kpiVal:   { fontSize:'24px', fontWeight:'800', color:'#1B2A5E' },
  kpiLabel: { fontSize:'11px', color:'#6c757d', marginTop:'2px' },
  tabs: { display:'flex', gap:'4px', borderBottom:'2px solid #e8f0fb', marginBottom:'16px' },
  tab: (activo) => ({ padding:'9px 20px', fontSize:'13px', fontWeight:activo?'700':'400', cursor:'pointer', border:'none', borderRadius:'8px 8px 0 0', background:activo?'#fff':'transparent', color:activo?'#1B2A5E':'#6c757d', borderBottom:activo?'2px solid #fff':'none', marginBottom:activo?'-2px':'0' }),

  // ── Agrupación por nivel (v4: rediseño visual) ──
  nivelBloque:  { marginBottom:'18px', border:'1px solid #e3e8ef', borderRadius:'12px', overflow:'hidden', background:'#fff' },
  nivelHeader:  (colapsado) => ({
    display:'flex', alignItems:'center', justifyContent:'space-between',
    padding:'13px 18px', cursor:'pointer', userSelect:'none',
    background: colapsado ? '#f7f9fc' : 'linear-gradient(135deg,#eef2f9,#f7f9fc)',
    borderBottom: colapsado ? 'none' : '1px solid #e3e8ef',
  }),
  nivelTitulo:  { display:'flex', alignItems:'center', gap:'10px', fontSize:'14px', fontWeight:'800', color:'#1B2A5E' },
  nivelChevron: (colapsado) => ({ display:'inline-block', fontSize:'11px', color:'#8a93a3', transition:'transform .15s', transform: colapsado ? 'rotate(-90deg)' : 'rotate(0deg)' }),
  nivelResumenWrap: { display:'flex', gap:'6px', flexWrap:'wrap' },
  nivelChip: (color) => ({ fontSize:'10.5px', fontWeight:'700', padding:'3px 9px', borderRadius:'99px', background:color.bg, color:color.color, whiteSpace:'nowrap' }),
  nivelBody:    { padding:'16px 18px' },
  gridCompacto: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(148px,1fr))', gap:'10px' },
  habCardCompacta: (estado) => ({
    border:`1.5px solid ${ESTADO_COLOR[estado]?.border||'#dde3ea'}`,
    borderRadius:'9px', padding:'11px 12px', background:'#fff',
    cursor:'pointer', transition:'box-shadow .12s, transform .12s',
    display:'flex', flexDirection:'column', gap:'6px',
  }),
  habCardTop: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'6px' },
  habNumCompacto: { fontSize:'16px', fontWeight:'800', color:'#1B2A5E', lineHeight:1 },
  estadoDot: (estado) => ({ width:'9px', height:'9px', borderRadius:'50%', background:ESTADO_COLOR[estado]?.color||'#888', flexShrink:0, marginTop:'4px' }),
  habTipoCompacto: (tipo) => ({ fontSize:'9.5px', fontWeight:'700', color:TIPO_COLOR[tipo]?.color||'#666', textTransform:'uppercase', letterSpacing:'.03em' }),
  habInfoLinea: { fontSize:'10.5px', color:'#6c757d', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:'4px' },
  habCardAcciones: { display:'flex', gap:'5px', marginTop:'2px' },
  btnMini: { flex:1, border:'none', borderRadius:'6px', fontSize:'10px', fontWeight:'700', padding:'5px 0', cursor:'pointer' },
};

const HAB_VACÍA = { numero:'', tipo:'General', precio_dia:'0', descripcion:'' };
const CLI_VACÍA = { nombre:'', especialidad:'' };
const EST_VACÍA = { nombre:'', piso_area:'' };

// Infere el nivel/piso a partir del primer dígito del número de habitación.
// Ej: "101" → Nivel 1, "205" → Nivel 2. Lo que no siga el patrón numérico
// (quirófanos, salas especiales, etc.) cae en "Otras áreas".
function inferirNivel(numero) {
  const match = String(numero || '').trim().match(/^(\d)/);
  if (!match) return 'Otras áreas';
  return `Nivel ${match[1]}`;
}

function agruparPorNivel(lista) {
  const grupos = {};
  lista.forEach(h => {
    const nivel = inferirNivel(h.numero);
    if (!grupos[nivel]) grupos[nivel] = [];
    grupos[nivel].push(h);
  });
  // Ordena: Nivel 1, Nivel 2, ... numéricamente, y "Otras áreas" al final
  return Object.keys(grupos)
    .sort((a, b) => {
      if (a === 'Otras áreas') return 1;
      if (b === 'Otras áreas') return -1;
      return a.localeCompare(b, undefined, { numeric:true });
    })
    .map(nivel => ({ nivel, items: grupos[nivel].sort((a,b) => String(a.numero).localeCompare(String(b.numero), undefined, { numeric:true })) }));
}

export default function Habitaciones({ usuario }) {
  const [habitaciones, setHabitaciones] = useState([]);
  const [clinicas,     setClinicas]     = useState([]);
  const [filtro,       setFiltro]       = useState('Todas');
  const [cargando,     setCargando]     = useState(true);
  const [tabActiva,    setTabActiva]    = useState('habitaciones');
  const [nivelesColapsados, setNivelesColapsados] = useState({}); // { 'Nivel 1': true, ... }

  // Modal habitación
  const [selHab,       setSelHab]       = useState(null);  // para cambiar estado
  const [nuevoEstado,  setNuevoEstado]  = useState('');
  const [nuevoUsoOtro, setNuevoUsoOtro] = useState('');    // v3: descripción cuando estado=Otra
  const [modalHabForm, setModalHabForm] = useState(null);  // null | 'nueva' | objeto (editar)
  const [habForm,      setHabForm]      = useState(HAB_VACÍA);
  const [guardandoHab, setGuardandoHab] = useState(false);

  // Modal clínica
  const [modalCliForm, setModalCliForm] = useState(null);
  const [cliForm,      setCliForm]      = useState(CLI_VACÍA);
  const [selCli,       setSelCli]       = useState(null);
  const [nuevoEstadoCli, setNuevoEstadoCli] = useState('');
  const [guardandoCli, setGuardandoCli] = useState(false);

  // Estaciones de enfermería
  const [estaciones,      setEstaciones]      = useState([]);
  const [modalEstForm,    setModalEstForm]    = useState(null); // null | 'nueva' | objeto (editar)
  const [estForm,         setEstForm]         = useState(EST_VACÍA);
  const [guardandoEst,    setGuardandoEst]    = useState(false);
  const [modalAsignarEst, setModalAsignarEst] = useState(null); // habitación seleccionada para asignar

  const cargar = useCallback(async () => {
    setCargando(true);
    const [rH, rC, rE] = await Promise.all([
      api.habitaciones.listar(),
      api.clinicas.listar(),
      api.estaciones.listar(),
    ]);
    if (rH.ok) setHabitaciones(rH.data);
    if (rC.ok) setClinicas(rC.data);
    if (rE.ok) setEstaciones(rE.data);
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Habitaciones ─────────────────────────────────────────
  const abrirEstadoHab = (h) => {
    setSelHab(h);
    setNuevoEstado(h.estado);
    setNuevoUsoOtro(h.uso_otro || '');
  };

  const guardarEstadoHab = async () => {
    if (!selHab) return;
    // Siempre guardar — evita el bug donde uso_otro no se persiste
    // cuando el estado es 'Otra' y solo cambia el texto
    await api.habitaciones.cambiarEstado({
      id:       selHab.id,
      estado:   nuevoEstado,
      uso_otro: nuevoEstado === 'Otra' ? (nuevoUsoOtro.trim() || null) : null,
    });
    setSelHab(null); cargar();
  };

  const abrirFormHab = (h = null) => {
    setHabForm(h ? {
      numero:    h.numero,
      tipo:      h.tipo,
      precio_dia: String(h.precio_dia ?? '0'),
      descripcion: h.descripcion || '',
    } : HAB_VACÍA);
    setModalHabForm(h || 'nueva');
  };

  const guardarHab = async () => {
    if (!habForm.numero) return;
    setGuardandoHab(true);
    const payload = { ...habForm, precio_dia: 0 };
    if (modalHabForm === 'nueva') {
      await api.habitaciones.crear(payload);
    } else {
      await api.habitaciones.editar({ id: modalHabForm.id, ...payload });
    }
    setGuardandoHab(false); setModalHabForm(null); cargar();
  };

  // ── Clínicas ────────────────────────────────────────────
  const abrirEstadoCli = (c) => { setSelCli(c); setNuevoEstadoCli(c.estado); };

  const guardarEstadoCli = async () => {
    if (!selCli || nuevoEstadoCli === selCli.estado) { setSelCli(null); return; }
    await api.clinicas.cambiarEstado({ id:selCli.id, estado:nuevoEstadoCli });
    setSelCli(null); cargar();
  };

  const abrirFormCli = (c = null) => {
    setCliForm(c ? { nombre:c.nombre, especialidad:c.especialidad||'' } : CLI_VACÍA);
    setModalCliForm(c || 'nueva');
  };

  const guardarCli = async () => {
    if (!cliForm.nombre) return;
    setGuardandoCli(true);
    if (modalCliForm === 'nueva') {
      await api.clinicas.crear(cliForm);
    } else {
      await api.clinicas.editar({ id:modalCliForm.id, ...cliForm });
    }
    setGuardandoCli(false); setModalCliForm(null); cargar();
  };

  // ── Estaciones de enfermería ─────────────────────────────
  const abrirFormEst = (e = null) => {
    setEstForm(e ? { nombre:e.nombre, piso_area:e.piso_area||'' } : EST_VACÍA);
    setModalEstForm(e || 'nueva');
  };

  const guardarEst = async () => {
    if (!estForm.nombre.trim()) return;
    setGuardandoEst(true);
    if (modalEstForm === 'nueva') {
      await api.estaciones.crear(estForm);
    } else {
      await api.estaciones.editar({ id: modalEstForm.id, ...estForm });
    }
    setGuardandoEst(false); setModalEstForm(null); cargar();
  };

  const toggleActivaEst = async (e) => {
    await api.estaciones.editar({ id: e.id, nombre: e.nombre, piso_area: e.piso_area, activo: e.activo ? 0 : 1 });
    cargar();
  };

  const asignarHabitacionAEstacion = async (estacionId) => {
    if (!modalAsignarEst) return;
    await api.estaciones.asignarHabitacion(modalAsignarEst.id, { estacion_id: estacionId || null });
    setModalAsignarEst(null); cargar();
  };

  const filtradas = filtro === 'Todas' ? habitaciones : habitaciones.filter(h => h.estado === filtro);
  const conteo = Object.fromEntries(['Libre','Ocupada','Limpieza','Mantenimiento','Otra'].map(e => [e, habitaciones.filter(h=>h.estado===e).length]));
  const conteoCli = Object.fromEntries(['Libre','Ocupada','Mantenimiento'].map(e => [e, clinicas.filter(c=>c.estado===e).length]));
  const nivelesAgrupados = agruparPorNivel(filtradas);

  const toggleNivel = (nivel) => {
    setNivelesColapsados(prev => ({ ...prev, [nivel]: !prev[nivel] }));
  };

  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      {/* ══ Header módulo — estandarizado ══ */}
      <div style={{ background:'linear-gradient(135deg,#1B2A5E,#0f1c40)', padding:'16px 24px 0' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          🛏 Módulo de Habitaciones
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)', marginBottom:'12px' }}>
          Control de ocupación · Habitaciones y clínicas
        </div>
        <div style={{ display:'flex', gap:'2px' }}>
          {[
            { id:'habitaciones', label:'🛏 Habitaciones' },
            { id:'clinicas',     label:'🏥 Clínicas' },
            { id:'estaciones',   label:'📦 Estaciones de Enfermería' },
          ].map(t => (
            <button key={t.id} onClick={() => setTabActiva(t.id)}
              style={{ padding:'9px 20px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'700',
                borderRadius:'8px 8px 0 0', fontFamily:'inherit',
                background: tabActiva === t.id ? '#fff' : 'rgba(255,255,255,.15)',
                color:      tabActiva === t.id ? '#1B2A5E' : '#fff' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={S.pagina}>

      {/* KPIs */}
      <div style={S.resumen}>
        {[
          { label:'Hab. Libres',   val:conteo.Libre||0,   color:'#155724' },
          { label:'Hab. Ocupadas', val:conteo.Ocupada||0, color:'#721c24' },
          { label:'Clín. Libres',  val:conteoCli.Libre||0, color:'#004085' },
          { label:'Clín. Ocupadas',val:conteoCli.Ocupada||0, color:'#856404' },
        ].map(k => (
          <div key={k.label} style={S.kpi}>
            <div style={{ ...S.kpiVal, color:k.color }}>{k.val}</div>
            <div style={S.kpiLabel}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* ══ TAB HABITACIONES ══ */}
      {tabActiva === 'habitaciones' && (
        <div style={S.card}>
          <div style={S.cardTitulo}>
            <span>Habitaciones ({habitaciones.length})</span>
            <div style={{ display:'flex', gap:'8px' }}>
              <button style={S.btnPrimario} onClick={() => abrirFormHab()}>+ Nueva habitación</button>
              <button style={S.btnSecundario} onClick={cargar}>Actualizar</button>
            </div>
          </div>

          {/* Filtros */}
          <div style={{ marginBottom:'16px' }}>
            {['Todas','Libre','Ocupada','Limpieza','Mantenimiento','Otra'].map(f => (
              <button key={f} style={S.btnEstado(filtro===f)} onClick={() => setFiltro(f)}>
                {f} {f!=='Todas' ? `(${conteo[f]||0})` : `(${habitaciones.length})`}
              </button>
            ))}
          </div>

          {cargando ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#6c757d' }}>Cargando...</div>
          ) : nivelesAgrupados.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#6c757d' }}>No hay habitaciones que coincidan con el filtro.</div>
          ) : (
            nivelesAgrupados.map(({ nivel, items }) => {
              const colapsado = !!nivelesColapsados[nivel];
              const conteoNivel = Object.fromEntries(['Libre','Ocupada','Limpieza','Mantenimiento','Otra'].map(e => [e, items.filter(h=>h.estado===e).length]));
              return (
                <div key={nivel} style={S.nivelBloque}>
                  <div style={S.nivelHeader(colapsado)} onClick={() => toggleNivel(nivel)}>
                    <div style={S.nivelTitulo}>
                      <span style={S.nivelChevron(colapsado)}>▼</span>
                      <span>{nivel === 'Otras áreas' ? '🏷' : '🏢'} {nivel}</span>
                      <span style={{ fontSize:'11px', fontWeight:'600', color:'#8a93a3' }}>({items.length})</span>
                    </div>
                    <div style={S.nivelResumenWrap}>
                      {['Libre','Ocupada','Limpieza','Mantenimiento','Otra'].map(e => (
                        conteoNivel[e] > 0 && (
                          <span key={e} style={S.nivelChip(ESTADO_COLOR[e])}>{conteoNivel[e]} {e}</span>
                        )
                      ))}
                    </div>
                  </div>
                  {!colapsado && (
                    <div style={S.nivelBody}>
                      <div style={S.gridCompacto}>
                        {items.map(h => (
                          <div key={h.id} style={S.habCardCompacta(h.estado)}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow='0 3px 10px rgba(27,42,94,0.12)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow='none'; e.currentTarget.style.transform='translateY(0)'; }}>
                            <div style={S.habCardTop}>
                              <div>
                                <div style={S.habNumCompacto}>{h.numero}</div>
                                <div style={S.habTipoCompacto(h.tipo)}>{h.tipo}</div>
                              </div>
                              <span style={S.estadoDot(h.estado)} title={h.estado}></span>
                            </div>
                            {h.estado === 'Otra' && h.uso_otro && (
                              <div style={S.habInfoLinea} title={h.uso_otro}>📌 {h.uso_otro}</div>
                            )}
                            {h.paciente_actual && (
                              <div style={S.habInfoLinea} title={h.paciente_actual}>👤 {h.paciente_actual}</div>
                            )}
                            <div style={S.habInfoLinea} title={h.estacion_nombre || 'Sin estación asignada'}>
                              📦 {h.estacion_nombre || <span style={{ color:'#c0392b' }}>Sin estación</span>}
                            </div>
                            <div style={S.habCardAcciones}>
                              <button style={{ ...S.btnMini, background:'#1B2A5E', color:'#fff' }} onClick={() => abrirEstadoHab(h)}>Estado</button>
                              <button style={{ ...S.btnMini, background:'#f0f4f8', color:'#495057' }} onClick={() => abrirFormHab(h)}>Editar</button>
                              <button style={{ ...S.btnMini, background:'#e8f0fb', color:'#1B2A5E' }} onClick={() => setModalAsignarEst(h)}>Estación</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* ══ TAB CLÍNICAS ══ */}
      {tabActiva === 'clinicas' && (
        <div style={S.card}>
          <div style={S.cardTitulo}>
            <span>Clínicas ({clinicas.length})</span>
            <div style={{ display:'flex', gap:'8px' }}>
              <button style={S.btnPrimario} onClick={() => abrirFormCli()}>+ Nueva clínica</button>
              <button style={S.btnSecundario} onClick={cargar}>Actualizar</button>
            </div>
          </div>

          {cargando ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#6c757d' }}>Cargando...</div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'12px' }}>
              {clinicas.map(c => (
                <div key={c.id} style={{
                  border:`2px solid ${c.estado==='Libre'?'#c3e6cb':c.estado==='Ocupada'?'#f5c6cb':'#d6d8db'}`,
                  borderRadius:'10px', padding:'16px', background:'#fff',
                }}>
                  <div style={{ fontSize:'14px', fontWeight:'700', color:'#1B2A5E', marginBottom:'4px' }}>{c.nombre}</div>
                  {c.especialidad && <div style={{ fontSize:'12px', color:'#6c757d', marginBottom:'8px' }}>{c.especialidad}</div>}
                  <span style={S.estadoPill(c.estado)}>{c.estado}</span>
                  <div style={{ display:'flex', gap:'6px', marginTop:'10px' }}>
                    <button style={{ ...S.btnAzul, fontSize:'10px', padding:'4px 10px' }} onClick={() => abrirEstadoCli(c)}>Estado</button>
                    <button style={{ ...S.btnSecundario, fontSize:'10px', padding:'4px 10px' }} onClick={() => abrirFormCli(c)}>Editar</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ TAB ESTACIONES DE ENFERMERÍA ══ */}
      {tabActiva === 'estaciones' && (
        <div style={S.card}>
          <div style={S.cardTitulo}>
            <span>Estaciones de Enfermería ({estaciones.length})</span>
            <div style={{ display:'flex', gap:'8px' }}>
              <button style={S.btnPrimario} onClick={() => abrirFormEst()}>+ Nueva estación</button>
              <button style={S.btnSecundario} onClick={cargar}>Actualizar</button>
            </div>
          </div>

          <div style={{ fontSize:'11px', color:'#6c757d', marginBottom:'14px' }}>
            Cada estación representa un piso/área con stock básico propio (analgésicos, insumos, etc.).
            Asigna habitaciones a una estación desde la pestaña "Habitaciones" → botón "Estación".
          </div>

          {cargando ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#6c757d' }}>Cargando...</div>
          ) : estaciones.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#6c757d' }}>
              Aún no hay estaciones registradas. Crea la primera con "+ Nueva estación".
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'12px' }}>
              {estaciones.map(e => {
                const numHabs = habitaciones.filter(h => h.estacion_id === e.id).length;
                return (
                  <div key={e.id} style={{
                    border:`2px solid ${e.activo ? '#c3e6cb' : '#d6d8db'}`,
                    borderRadius:'10px', padding:'16px', background:'#fff',
                  }}>
                    <div style={{ fontSize:'14px', fontWeight:'700', color:'#1B2A5E', marginBottom:'4px' }}>{e.nombre}</div>
                    {e.piso_area && <div style={{ fontSize:'12px', color:'#6c757d', marginBottom:'8px' }}>{e.piso_area}</div>}
                    <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'8px' }}>
                      <span style={S.estadoPill(e.activo ? 'Libre' : 'Mantenimiento')}>{e.activo ? 'Activa' : 'Inactiva'}</span>
                      <span style={{ ...S.estadoPill('Otra'), background:'#e8f0fb', color:'#1B2A5E' }}>{numHabs} habitación(es)</span>
                    </div>
                    <div style={{ display:'flex', gap:'6px', marginTop:'10px' }}>
                      <button style={{ ...S.btnAzul, fontSize:'10px', padding:'4px 10px' }} onClick={() => abrirFormEst(e)}>Editar</button>
                      <button style={{ ...S.btnSecundario, fontSize:'10px', padding:'4px 10px' }} onClick={() => toggleActivaEst(e)}>
                        {e.activo ? 'Desactivar' : 'Activar'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Modal: cambiar estado habitación ── */}
      {selHab && (
        <div style={S.modal} onClick={() => setSelHab(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'16px', fontWeight:'800', color:'#1B2A5E', marginBottom:'4px' }}>Habitación {selHab.numero}</div>
            <div style={{ fontSize:'13px', color:'#6c757d', marginBottom:'20px' }}>
              {selHab.tipo}
              {selHab.numero?.startsWith('QUIROFANO') ? ' · precio según caso' : ' · precio pendiente de definir'}
              {selHab.paciente_actual && ` · ${selHab.paciente_actual}`}
              {selHab.uso_otro && <span style={{ color:'#1B2A5E', fontWeight:'600' }}> · {selHab.uso_otro}</span>}
            </div>
            <div style={{ fontSize:'11px', fontWeight:'700', color:'#6c757d', marginBottom:'10px', textTransform:'uppercase' }}>Cambiar estado</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'14px' }}>
              {Object.keys(ESTADO_COLOR).map(e => (
                <button key={e}
                  style={{ padding:'8px 16px', borderRadius:'7px', fontSize:'13px', fontWeight:'600', cursor:'pointer',
                    border:`2px solid ${nuevoEstado===e ? ESTADO_COLOR[e].border : '#dde3ea'}`,
                    background: nuevoEstado===e ? ESTADO_COLOR[e].bg : '#fff',
                    color:      nuevoEstado===e ? ESTADO_COLOR[e].color : '#495057' }}
                  onClick={() => setNuevoEstado(e)}>{e}
                </button>
              ))}
            </div>
            {/* Textbox uso libre cuando estado = Otra */}
            {nuevoEstado === 'Otra' && (
              <div style={{ marginBottom:'16px' }}>
                <label style={S.label}>Especificar uso actual</label>
                <input style={S.input} value={nuevoUsoOtro}
                  onChange={e => setNuevoUsoOtro(e.target.value)}
                  placeholder="Ej: Oficina, Almacén, Reuniones..." autoFocus />
                <div style={{ fontSize:'10px', color:'#6c757d', marginTop:'4px' }}>
                  Describe el uso actual de esta habitación
                </div>
              </div>
            )}
            <div style={{ display:'flex', gap:'8px' }}>
              <button style={S.btnPrimario} onClick={guardarEstadoHab}>Guardar</button>
              <button style={S.btnSecundario} onClick={() => setSelHab(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: crear / editar habitación ── */}
      {modalHabForm !== null && (
        <div style={S.modal} onClick={() => setModalHabForm(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'15px', fontWeight:'800', color:'#1B2A5E', marginBottom:'20px' }}>
              {modalHabForm === 'nueva' ? 'Nueva habitación' : `Editar: Hab. ${modalHabForm.numero}`}
            </div>
            <div style={S.campo}>
              <label style={S.label}>Número / Nombre *</label>
              <input style={S.input} value={habForm.numero} onChange={e => setHabForm(f=>({...f,numero:e.target.value}))} placeholder="Ej: 205, UCI-01, QUIROFANO-01" />
            </div>
            <div style={S.campo}>
              <label style={S.label}>Tipo *</label>
              <select style={S.input} value={habForm.tipo} onChange={e => setHabForm(f=>({...f,tipo:e.target.value}))}>
                {TIPOS_HAB.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <div style={{ fontSize:'10px', color:'#6c757d', marginTop:'3px' }}>
                El precio queda en $0.00 por defecto. Se definirá después en cada caso o habitación.
              </div>
            </div>
            <div style={S.campo}>
              <label style={S.label}>Descripción (opcional)</label>
              <input style={S.input} value={habForm.descripcion} onChange={e => setHabForm(f=>({...f,descripcion:e.target.value}))} placeholder="Vista, piso, características..." />
            </div>
            <div style={{ display:'flex', gap:'8px', marginTop:'6px' }}>
              <button style={S.btnPrimario} onClick={guardarHab} disabled={guardandoHab}>
                {guardandoHab ? 'Guardando...' : 'Guardar'}
              </button>
              <button style={S.btnSecundario} onClick={() => setModalHabForm(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: cambiar estado clínica ── */}
      {selCli && (
        <div style={S.modal} onClick={() => setSelCli(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'16px', fontWeight:'800', color:'#1B2A5E', marginBottom:'4px' }}>{selCli.nombre}</div>
            <div style={{ fontSize:'13px', color:'#6c757d', marginBottom:'20px' }}>{selCli.especialidad||'Sin especialidad'}</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'20px' }}>
              {['Libre','Ocupada','Mantenimiento'].map(e => (
                <button key={e} style={{ padding:'8px 16px', borderRadius:'7px', fontSize:'13px', fontWeight:'600', cursor:'pointer', border:`2px solid ${nuevoEstadoCli===e?ESTADO_COLOR[e].border:'#dde3ea'}`, background:nuevoEstadoCli===e?ESTADO_COLOR[e].bg:'#fff', color:nuevoEstadoCli===e?ESTADO_COLOR[e].color:'#495057' }}
                  onClick={() => setNuevoEstadoCli(e)}>{e}</button>
              ))}
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button style={S.btnPrimario} onClick={guardarEstadoCli}>Guardar</button>
              <button style={S.btnSecundario} onClick={() => setSelCli(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: crear / editar clínica ── */}
      {modalCliForm !== null && (
        <div style={S.modal} onClick={() => setModalCliForm(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'15px', fontWeight:'800', color:'#1B2A5E', marginBottom:'20px' }}>
              {modalCliForm === 'nueva' ? 'Nueva clínica' : `Editar: ${modalCliForm.nombre}`}
            </div>
            <div style={S.campo}>
              <label style={S.label}>Nombre *</label>
              <input style={S.input} value={cliForm.nombre} onChange={e => setCliForm(f=>({...f,nombre:e.target.value}))} placeholder="Ej: Clínica 1 — Medicina General" />
            </div>
            <div style={S.campo}>
              <label style={S.label}>Especialidad</label>
              <input style={S.input} value={cliForm.especialidad} onChange={e => setCliForm(f=>({...f,especialidad:e.target.value}))} placeholder="Ej: Pediatría, Ginecología..." />
            </div>
            <div style={{ display:'flex', gap:'8px', marginTop:'6px' }}>
              <button style={S.btnPrimario} onClick={guardarCli} disabled={guardandoCli}>
                {guardandoCli ? 'Guardando...' : 'Guardar'}
              </button>
              <button style={S.btnSecundario} onClick={() => setModalCliForm(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Modal: crear / editar estación de enfermería ── */}
      {modalEstForm !== null && (
        <div style={S.modal} onClick={() => setModalEstForm(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'15px', fontWeight:'800', color:'#1B2A5E', marginBottom:'20px' }}>
              {modalEstForm === 'nueva' ? 'Nueva estación de enfermería' : `Editar: ${modalEstForm.nombre}`}
            </div>
            <div style={S.campo}>
              <label style={S.label}>Nombre *</label>
              <input style={S.input} value={estForm.nombre} onChange={e => setEstForm(f=>({...f,nombre:e.target.value}))} placeholder="Ej: Estación 1er Piso, UCI..." />
            </div>
            <div style={S.campo}>
              <label style={S.label}>Piso / Área</label>
              <input style={S.input} value={estForm.piso_area} onChange={e => setEstForm(f=>({...f,piso_area:e.target.value}))} placeholder="Ej: 1er Piso, Ala Norte..." />
            </div>
            <div style={{ display:'flex', gap:'8px', marginTop:'6px' }}>
              <button style={S.btnPrimario} onClick={guardarEst} disabled={guardandoEst}>
                {guardandoEst ? 'Guardando...' : 'Guardar'}
              </button>
              <button style={S.btnSecundario} onClick={() => setModalEstForm(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: asignar habitación a una estación ── */}
      {modalAsignarEst && (
        <div style={S.modal} onClick={() => setModalAsignarEst(null)}>
          <div style={S.modalCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:'16px', fontWeight:'800', color:'#1B2A5E', marginBottom:'4px' }}>
              Habitación {modalAsignarEst.numero}
            </div>
            <div style={{ fontSize:'13px', color:'#6c757d', marginBottom:'20px' }}>
              Asigna a qué estación de enfermería pertenece esta habitación.
            </div>
            {estaciones.length === 0 ? (
              <div style={{ fontSize:'12px', color:'#c0392b', marginBottom:'16px' }}>
                No hay estaciones creadas todavía. Ve a la pestaña "Estaciones de Enfermería" para crear una primero.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px' }}>
                {estaciones.filter(e => e.activo).map(e => (
                  <button key={e.id}
                    style={{ padding:'10px 14px', borderRadius:'7px', fontSize:'13px', fontWeight:'600', cursor:'pointer', textAlign:'left',
                      border:`2px solid ${modalAsignarEst.estacion_id===e.id ? '#1B2A5E' : '#dde3ea'}`,
                      background: modalAsignarEst.estacion_id===e.id ? '#e8f0fb' : '#fff',
                      color:'#1B2A5E' }}
                    onClick={() => asignarHabitacionAEstacion(e.id)}>
                    {e.nombre}{e.piso_area ? ` — ${e.piso_area}` : ''}
                  </button>
                ))}
              </div>
            )}
            <div style={{ display:'flex', gap:'8px' }}>
              <button style={S.btnSecundario} onClick={() => asignarHabitacionAEstacion(null)}>Quitar asignación</button>
              <button style={S.btnSecundario} onClick={() => setModalAsignarEst(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
